import React from 'react';
import { CheckIcon, GlobeIcon, ScaleIcon } from 'lucide-react';
import { Seo } from '../components/ui/Seo';
import { PageHero } from '../components/ui/PageHero';
import { ButtonLink } from '../components/ui/ButtonLink';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Accordion } from '../components/ui/Accordion';
import { CcwStepsList } from '../components/shared/CcwStepsList';
import { CtaBand } from '../components/shared/CtaBand';
import { CcwNotice } from '../components/home/CcwExplainer';
import { ccwFaqs } from '../data/faqs';
import { IMAGES } from '../data/images';
import { BOOKING_URL } from '../data/site';
const comparison = [{
  label: 'Course length',
  initial: '8 hours',
  renewal: '4 hours'
}, {
  label: 'Classroom schedule',
  initial: '9:00am – 6:00pm',
  renewal: '9:00am – 1:00pm'
}, {
  label: 'Live-fire qualification',
  initial: 'Yes, after classroom',
  renewal: 'Yes, after classroom'
}, {
  label: 'Written exam',
  initial: 'Yes',
  renewal: 'Yes'
}, {
  label: 'Multi-state curriculum',
  initial: 'Included',
  renewal: 'Nevada renewal'
}, {
  label: 'Who it’s for',
  initial: 'First-time applicants & non-residents',
  renewal: 'Current Nevada permit holders'
}, {
  label: 'Tuition',
  initial: '$150',
  renewal: '$85'
}];
const laws = [{
  title: 'Use of deadly force',
  body: 'When Nevada law allows deadly force, and when it does not.'
}, {
  title: 'The force continuum',
  body: 'Matching your response to the level of threat.'
}, {
  title: 'Criminal liability',
  body: 'What happens legally after a defensive incident.'
}, {
  title: 'Civil liability',
  body: 'How civil suits work and how to protect yourself.'
}];
export function CcwPermits() {
  return <>
      <Seo title="Nevada CCW Permit Class & Renewal" description="Everything you need for your Nevada CCW: sheriff-approved Las Vegas CCW class, Nevada CCW renewal, non-resident permits and the laws covered." data-id="element-647" data-mp="1jp00ujygxbrv" />
      <PageHero title="Everything you need for your Nevada CCW" sub="Sheriff-approved since 2010. New permits, renewals and non-resident applicants — taught in plain English." image={IMAGES.classroom} crumbs={[{
      label: 'CCW Permits'
    }]} data-id="element-648" data-mp="2v4evvi749tkc">
        <ButtonLink href={BOOKING_URL} size="lg" data-id="element-649" data-mp="3c7cbige812wh">
          Book Your CCW Class
        </ButtonLink>
        <ButtonLink to="/courses/nv-ccw-renewal" size="lg" variant="outline-light" data-id="element-650" data-mp="3h4cgenzc3zm0">
          Renewal Course
        </ButtonLink>
      </PageHero>

      <section className="gutter section bg-white" aria-labelledby="steps-title" data-id="element-651" data-mp="2jfslodn4cqli">
        <SectionHeading id="steps-title" title="The 4-Step Process" data-id="element-652" data-mp="2ogzt2blcnnrn" />
        <div className="mt-14" data-id="element-653" data-mp="2terv3yfpkq9k">
          <CcwStepsList data-id="element-654" data-mp="2tdq94yx5mjd2" />
        </div>
        <div className="mt-12" data-id="element-655" data-mp="2y71j38x9jg97">
          <CcwNotice data-id="element-656" data-mp="2y7b92wib5new" />
        </div>
      </section>

      <section className="gutter section bg-mist" aria-labelledby="compare-title" data-id="element-657" data-mp="20r5i5zklc9a1">
        <SectionHeading id="compare-title" title="New Permit vs. Renewal" data-id="element-658" data-mp="20pb7rf6y3zk7" />
        <div className="mt-10 overflow-x-auto rounded-[4px] bg-white ring-1 ring-navy/10" data-id="element-659" data-mp="25onqpoup4mhi">
          <table className="w-full min-w-[40rem] text-left" data-id="element-660" data-mp="1y67jeja7ep49">
            <caption className="sr-only" data-id="element-661" data-mp="1t73cjzar53h3">Comparison of the initial CCW course and the renewal course</caption>
            <thead data-id="element-662" data-mp="1o7qo5a48foce">
              <tr className="bg-navy text-white" data-id="element-663" data-mp="1o78mtz2pqjxm">
                <th scope="col" className="w-1/3 px-6 py-5 font-display text-sm font-semibold uppercase tracking-[0.12em] text-chrome-300" data-id="element-664" data-mp="1j9p6mtmgfx31">
                  &nbsp;
                </th>
                <th scope="col" className="px-6 py-5 font-display text-xl font-semibold uppercase" data-id="element-665" data-mp="1f9ojmklaidkd">Multi-State CCW (Initial)</th>
                <th scope="col" className="px-6 py-5 font-display text-xl font-semibold uppercase" data-id="element-666" data-mp="15hiqv8n7rs44">NV CCW Renewal</th>
              </tr>
            </thead>
            <tbody data-id="element-667" data-mp="10guq3v96nwwp">
              {comparison.map(row => <tr key={row.label} className="border-t border-navy/10" data-id="element-668" data-mp="vit9ri356box">
                  <th scope="row" className="px-6 py-4 font-semibold text-navy" data-id="element-669" data-mp="rhr68p26zdh5">{row.label}</th>
                  <td className="px-6 py-4 text-ink" data-id="element-670" data-mp="1g8eyo1vqrs40">{row.initial}</td>
                  <td className="px-6 py-4 text-ink" data-id="element-671" data-mp="1gb9jvvmkxgyh">{row.renewal}</td>
                </tr>)}
              <tr className="border-t border-navy/10" data-id="element-672" data-mp="17glem5h07z9o">
                <td className="px-6 py-5" data-id="element-673" data-mp="17ftuprwrrpz8" />
                <td className="px-6 py-5" data-id="element-674" data-mp="1bi702f7ltlvh">
                  <ButtonLink href={BOOKING_URL} size="sm" data-id="element-675" data-mp="1bh5cxcdbd8xe">Book Initial</ButtonLink>
                </td>
                <td className="px-6 py-5" data-id="element-676" data-mp="xhnudeqm2rwh">
                  <ButtonLink href={BOOKING_URL} size="sm" data-id="element-677" data-mp="xks4esqrchv2">Book Renewal</ButtonLink>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="gutter section grid gap-14 bg-white lg:grid-cols-2" aria-label="Non-residents and laws covered" data-id="element-678" data-mp="oju16co58edj">
        <div data-id="element-679" data-mp="2iw1ob3u83qyr">
          <GlobeIcon className="h-8 w-8 text-navy" aria-hidden="true" data-id="element-680" data-mp="2iv8sgxwsylve" />
          <h2 className="t-h2 mt-5 text-navy" data-id="element-681" data-mp="2itxj8ikl8lww">Non-residents welcome</h2>
          <span className="red-rule mt-5" aria-hidden="true" data-id="element-682" data-mp="2mwkih4jl9944" />
          <p className="t-lead mt-6 max-w-[40rem] text-steel" data-id="element-683" data-mp="b98pa7ih7l4a">
            You don’t have to live in Nevada to carry here. Take our course in Las Vegas, then apply with any Nevada county sheriff’s office. Our multi-state curriculum also supports applications for additional state permits.
          </p>
          <ul className="mt-6 space-y-2" data-id="element-684" data-mp="g821zxpxv83e">
            {['Apply with any Nevada county sheriff', 'Same 8-hour course and qualification', 'Popular with frequent Las Vegas visitors'].map(t => <li key={t} className="flex gap-3 text-ink" data-id="element-685" data-mp="pzpm1dk61muu">
                <CheckIcon className="mt-0.5 h-5 w-5 text-accent" aria-hidden="true" data-id="element-686" data-mp="q197dikoio3h" /> {t}
              </li>)}
          </ul>
        </div>
        <div data-id="element-687" data-mp="z0nqty2fhyeh">
          <ScaleIcon className="h-8 w-8 text-navy" aria-hidden="true" data-id="element-688" data-mp="z049aigoxc0z" />
          <h2 className="t-h2 mt-5 text-navy" data-id="element-689" data-mp="35p97o5yybe3c">The law, in plain English</h2>
          <span className="red-rule mt-5" aria-hidden="true" data-id="element-690" data-mp="30r7nfhpsh8qa" />
          <dl className="mt-6 grid gap-x-8 sm:grid-cols-2" data-id="element-691" data-mp="30ssjxcvnz1qs">
            {laws.map(l => <div key={l.title} className="border-t border-navy/15 py-5" data-id="element-692" data-mp="14pnpdod9c5cj">
                <dt className="font-display text-xl font-semibold uppercase text-navy" data-id="element-693" data-mp="19np9mcmf6apl">{l.title}</dt>
                <dd className="mt-1 text-steel" data-id="element-694" data-mp="1el7laartn41a">{l.body}</dd>
              </div>)}
          </dl>
        </div>
      </section>

      <section className="gutter section bg-mist" aria-labelledby="ccw-faq-title" data-id="element-695" data-mp="1il9cfpzxkf2b">
        <div className="grid gap-12 lg:grid-cols-12" data-id="element-696" data-mp="1nmf8nxssukgl">
          <div className="lg:col-span-4" data-id="element-697" data-mp="1sl08hw45324i">
            <SectionHeading id="ccw-faq-title" title="CCW Questions" data-id="element-698" data-mp="1sjxkbvy7bdsa" />
            <p className="mt-6 text-xs text-steel" data-id="element-699" data-mp="1xdjo0yme07by">Not legal advice. Information is educational and subject to change; consult your county sheriff or an attorney for your situation.</p>
          </div>
          <div className="lg:col-span-8" data-id="element-700" data-mp="26e837wniodqf">
            <Accordion items={ccwFaqs} defaultOpen={0} data-id="element-701" data-mp="26bc07twtayv4" />
          </div>
        </div>
      </section>

      <CtaBand title="Get Your Nevada CCW This Week." sub="Classes run 7 days a week at Bass Pro Shops – Silverton." primaryLabel="Book Your CCW Class" data-id="element-702" data-mp="2b9x1lwnsvol5" />
    </>;
}